/* FatherGlorified 4.2.0 — purchase help and first-order offer. */
(() => {
  'use strict';
  const strings = window.FG_UI || {};
  const text = (key, fallback) => strings[key] || fallback;
  const ready = new WeakMap();
  const clean = el => (el?.textContent || '').replace(/\s+/g, ' ').trim();
  const sizePattern = /^(?:\d?X{0,3}S|S|M|L|X{1,5}L|[2-6]XL|\d{2,3}(?:[/-]\d{2,3})?)$/i;
  const labelMap = { 'length': 'Hossz', 'width': 'Szélesség', 'chest': 'Mellrész szélessége', 'shoulder': 'Vállszélesség', 'sleeve': 'Ujjhossz', 'sleeve length': 'Ujjhossz', 'chest width': 'Mellrész szélessége', 'bust': 'Mellrész', 'waist': 'Derék', 'hip': 'Csípő', 'inseam': 'Belső szárhossz' };
  const hu = document.documentElement.lang.toLowerCase().startsWith('hu');
  function label(s) {
    const base = s.replace(/\s*\((cm|inch|in)\)/ig, '').trim();
    return hu ? (labelMap[base.toLowerCase()] || s) : s;
  }
  function readTable(table) {
    const rows = Array.from(table.rows).filter(row => row.cells.length);
    if (rows.length < 2) return [];
    const top = Array.from(rows[0].cells);
    const sizesAcross = top.slice(1).every(cell => sizePattern.test(clean(cell)));
    if (sizesAcross && top.length > 1) {
      const unitRow = Array.from(rows[1].cells);
      const unitCells = unitRow.slice(1).map(clean).filter(Boolean);
      const hasUnits = unitCells.length > 0 && unitCells.every(value => /^(cm|in|inch|inches|hüvelyk)$/i.test(value));
      let offset = 1;
      return top.slice(1).map(cell => {
        const span = cell.colSpan || 1;
        const values = rows.slice(hasUnits ? 2 : 1).flatMap(row => {
          const cells = Array.from(row.cells);
          return Array.from({ length: span }, (_, n) => {
            const unit = hasUnits ? clean(unitRow[offset + n]).replace(/inches?/i, 'in') : (/\bcm\b/i.test(clean(row.cells[0])) ? 'cm' : '');
            return { label: label(clean(cells[0])), value: [clean(cells[offset + n]), unit].filter(Boolean).join(' ') };
          }).filter(v => v.value);
        });
        offset += span;
        return { size: clean(cell), values };
      });
    }
    return rows.slice(1).filter(row => sizePattern.test(clean(row.cells[0]))).map(row => ({
      size: clean(row.cells[0]),
      values: Array.from(row.cells).slice(1).map((cell, i) => ({
        label: label(clean(top[i + 1])),
        value: clean(cell) + (/\bcm\b/i.test(clean(top[i + 1])) ? ' cm' : /\binch/i.test(clean(top[i + 1])) ? ' in' : '')
      }))
    }));
  }
  function initSizes(root = document) {
    const helps = root.matches?.('[data-fg-size-help]') ? [root] : root.querySelectorAll?.('[data-fg-size-help]') || [];
    for (const help of helps) {
      const out = help.querySelector('[data-fg-size-output]');
      const source = help.querySelector('template[data-fg-size-source]');
      if (!out || !source) continue;
      const previous = ready.get(help);
      if (previous?.source === source.innerHTML) continue;
      if (previous?.picker) previous.picker.removeEventListener('change', previous.sync);
      ready.set(help, { source: source.innerHTML });
      const entries = Array.from(source.content.querySelectorAll('table')).flatMap(readTable);
      out.replaceChildren();
      if (!entries.length) {
        const p = document.createElement('p');
        p.textContent = text('size_unavailable', 'Ask us for this piece’s measurements before ordering.');
        out.append(p);
        help.querySelector('[data-fg-size-note]').hidden = true;
        continue;
      }
      const combined = new Map();
      for (const item of entries) {
        const prior = combined.get(item.size) || [];
        combined.set(item.size, prior.concat(item.values));
      }
      const nav = document.createElement('div');
      nav.className = 'fg-size-help__sizes';
      nav.setAttribute('role', 'group');
      nav.setAttribute('aria-label', text('size_choose', 'Choose a size to see measurements'));
      const dl = document.createElement('dl');
      dl.className = 'fg-size-help__measurements';
      dl.setAttribute('aria-live', 'polite');
      const show = size => {
        dl.replaceChildren();
        const seen = new Set();
        for (const measurement of combined.get(size) || []) {
          const key = measurement.label + measurement.value;
          if (seen.has(key)) continue;
          seen.add(key);
          const line = document.createElement('div');
          const dt = document.createElement('dt'); dt.textContent = measurement.label;
          const dd = document.createElement('dd'); dd.textContent = measurement.value;
          line.append(dt, dd); dl.append(line);
        }
        for (const b of nav.children) b.setAttribute('aria-pressed', String(b.textContent === size));
      };
      for (const size of combined.keys()) {
        const button = document.createElement('button');
        button.type = 'button'; button.textContent = size;
        button.addEventListener('click', e => { e.stopPropagation(); show(size); });
        nav.append(button);
      }
      out.append(nav, dl);
      const picker = help.closest('variant-picker');
      const sync = () => {
        const selected = Array.from(picker?.querySelectorAll('input:checked, option:checked') || []).map(e => e.value);
        show(selected.find(s => combined.has(s)) || combined.keys().next().value);
      };
      picker?.addEventListener('change', sync);
      ready.set(help, { source: source.innerHTML, picker, sync });
      sync();
    }
  }
  function init() {
    initSizes();
    let scheduled = false;
    new MutationObserver(records => {
      if (scheduled || !records.some(r => r.addedNodes.length)) return;
      scheduled = true;
      queueMicrotask(() => { scheduled = false; initSizes(); });
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('click', async e => {
      const button = e.target.closest('[data-fg-copy-code]');
      if (!button) return;
      let copied = false;
      try { await navigator.clipboard.writeText('new10'); copied = true; } catch (_) {
        const input = document.createElement('textarea'); input.value = 'new10'; input.style.cssText = 'position:fixed;opacity:0'; document.body.append(input); input.select();
        try { copied = !!document.execCommand?.('copy'); } catch (_) { copied = false; }
        input.remove(); button.focus();
      }
      const container = button.closest('.fg-discount-note, .fg-welcome-offer, .fg-purchase-help');
      let status = container?.querySelector('[data-fg-offer-status]');
      if (!status && container) { status = document.createElement('span'); status.dataset.fgOfferStatus = ''; status.setAttribute('role', 'status'); container.append(status); }
      if (status) status.textContent = copied ? text('copied', 'Code copied. Enter it at checkout.') : text('copy_fallback', 'Enter new10 at checkout.');
    });
    const offer = document.querySelector('[data-fg-welcome-offer]');
    if (!offer) return;
    const get = key => { try { return sessionStorage.getItem(key); } catch (_) { return null; } };
    const set = (key, value) => { try { sessionStorage.setItem(key, value); } catch (_) {} };
    if (get('fg-offer-v420-shown')) return;
    let elapsed = Number(get('fg-offer-v420-seconds')) || 0;
    const close = () => { offer.hidden = true; set('fg-offer-v420-shown', '1'); };
    offer.querySelector('[data-fg-close-offer]').addEventListener('click', close);
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && !offer.hidden) close(); });
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      elapsed += 1; set('fg-offer-v420-seconds', String(elapsed));
      // A shopping or consent dialog takes priority. Never cover the product or checkout journey.
      const blockingDialog = Array.from(document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"]')).some(node => node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
      if (elapsed < 60 || blockingDialog) return;
      offer.hidden = false; set('fg-offer-v420-shown', '1'); window.clearInterval(timer);
    }, 1000);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
